/**
 * <ry-number-select>
 *
 * Numeric input with buttons, drag, keyboard, wheel, and optional typing.
 *
 * Usage:
 * <ry-number-select min="0" max="100" value="50" step="1"></ry-number-select>
 * <ry-number-select min="0" max="10" step="0.5" arrows="end" editable></ry-number-select>
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

const DRAG_PX_PER_STEP = 15;
const CLICK_THRESHOLD = 3;

export class RyNumberSelect extends RyElement {
  #display: HTMLElement | null = null;
  #valueEl: HTMLElement | null = null;
  #inputEl: HTMLInputElement | null = null;
  #decrementBtn: HTMLElement | null = null;
  #incrementBtn: HTMLElement | null = null;

  #dragging = false;
  #startX = 0;
  #startY = 0;
  #startValue = 0;
  #editing = false;

  #repeatTimer: ReturnType<typeof setTimeout> | null = null;
  #repeatInterval: ReturnType<typeof setTimeout> | null = null;

  static observedAttributes = ['min', 'max', 'step', 'value', 'disabled', 'arrows', 'icons', 'drag', 'editable', 'wrap', 'label', 'prefix', 'suffix'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#cacheRefs();
    this.#bindEvents();
    this.#updateDisplay();
  }

  #buildMarkup(): void {
    const arrows = this.getAttribute('arrows') ?? 'both';
    const label = this.getAttribute('label');
    const stacked = arrows === 'stacked';

    const [decIcon, incIcon] = this.#getIcons(stacked);

    const showDecrement = arrows === 'both' || arrows === 'start' || stacked;
    const showIncrement = arrows === 'both' || arrows === 'end' || stacked;
    const prefix = this.getAttribute('prefix');
    const suffix = this.getAttribute('suffix');

    const decBtn = showDecrement
      ? `<button data-ry-target="decrement" class="ry-number-select__btn ry-number-select__decrement" aria-label="Decrease" tabindex="-1" type="button">${decIcon}</button>`
      : '';
    const incBtn = showIncrement
      ? `<button data-ry-target="increment" class="ry-number-select__btn ry-number-select__increment" aria-label="Increase" tabindex="-1" type="button">${incIcon}</button>`
      : '';
    const display = `
      <div data-ry-target="display" class="ry-number-select__display" tabindex="0" role="spinbutton"
           aria-valuenow="${this.value}" aria-valuemin="${this.min}" aria-valuemax="${this.max}"
           ${label ? `aria-label="${label}"` : ''}>
        ${prefix ? `<span data-ry-target="prefix" class="ry-number-select__affix">${prefix}</span>` : ''}
        <span data-ry-target="value" class="ry-number-select__value">${this.#formatValue(this.value)}</span>
        ${suffix ? `<span data-ry-target="suffix" class="ry-number-select__affix">${suffix}</span>` : ''}
        <input data-ry-target="input" class="ry-number-select__input" type="text" inputmode="decimal" tabindex="-1">
      </div>`;
    const labelEl = label ? `<span data-ry-target="label" class="ry-number-select__label">${label}</span>` : '';

    if (stacked) {
      // Vertical: increment on top, display in middle, decrement on bottom
      this.innerHTML = `${incBtn}${display}${decBtn}${labelEl}`;
    } else {
      this.innerHTML = `${decBtn}${display}${incBtn}${labelEl}`;
    }
  }

  #getIcons(stacked: boolean): [string, string] {
    const icons = this.getAttribute('icons') ?? 'plus-minus';

    if (icons === 'chevron') {
      if (stacked) {
        return [getIcon('chevron-down') ?? '▾', getIcon('chevron-up') ?? '▴'];
      }
      return [getIcon('chevron-left') ?? '‹', getIcon('chevron-right') ?? '›'];
    }

    if (icons === 'arrow') {
      if (stacked) {
        return [getIcon('chevron-down') ?? '▼', getIcon('chevron-up') ?? '▲'];
      }
      return [getIcon('chevron-left') ?? '◀', getIcon('chevron-right') ?? '▶'];
    }

    // Default: plus-minus
    return [getIcon('minus') ?? '−', getIcon('plus') ?? '+'];
  }

  #cacheRefs(): void {
    this.#display = this.$('[data-ry-target="display"]');
    this.#valueEl = this.$('[data-ry-target="value"]');
    this.#inputEl = this.$<HTMLInputElement>('[data-ry-target="input"]');
    this.#decrementBtn = this.$('[data-ry-target="decrement"]');
    this.#incrementBtn = this.$('[data-ry-target="increment"]');
  }

  #bindEvents(): void {
    // Buttons
    if (this.#decrementBtn) {
      this.on(this.#decrementBtn, 'mousedown', (e: MouseEvent) => { e.preventDefault(); this.#handleButtonDown(-1); });
      this.on(this.#decrementBtn, 'mouseup', () => this.#handleButtonUp());
      this.on(this.#decrementBtn, 'mouseleave', () => this.#handleButtonUp());
    }
    if (this.#incrementBtn) {
      this.on(this.#incrementBtn, 'mousedown', (e: MouseEvent) => { e.preventDefault(); this.#handleButtonDown(1); });
      this.on(this.#incrementBtn, 'mouseup', () => this.#handleButtonUp());
      this.on(this.#incrementBtn, 'mouseleave', () => this.#handleButtonUp());
    }

    // Drag on display
    if (this.#display) {
      this.on(this.#display, 'mousedown', this.#handleMouseDown);
    }
    this.on(document, 'mousemove', this.#handleMouseMove);
    this.on(document, 'mouseup', this.#handleMouseUp);

    // Touch
    if (this.#display) {
      this.on(this.#display, 'touchstart', this.#handleTouchStart);
    }
    this.on(document, 'touchmove', this.#handleTouchMove);
    this.on(document, 'touchend', this.#handleTouchEnd);

    // Keyboard
    if (this.#display) {
      this.on(this.#display, 'keydown', this.#handleKeydown);
    }

    // Wheel
    this.on(this, 'wheel', this.#handleWheel);

    // Double-click always enters edit mode
    if (this.#display) {
      this.on(this.#display, 'dblclick', () => this.#enterEditMode());
    }

    // Edit mode
    if (this.#inputEl) {
      this.on(this.#inputEl, 'blur', () => this.#commitEdit());
      this.on(this.#inputEl, 'keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); this.#commitEdit(); }
        if (e.key === 'Escape') { e.preventDefault(); this.#cancelEdit(); }
        e.stopPropagation();
      });
    }
  }

  // --- Button hold-to-repeat ---

  #handleButtonDown(direction: 1 | -1): void {
    if (this.disabled) return;

    const step = this.step || 1;
    this.#adjustValue(direction * step);
    this.#emitChange();

    this.#repeatTimer = setTimeout(() => {
      let interval = 150;
      const tick = (): void => {
        this.#adjustValue(direction * step);
        this.#emitInput();
        interval = Math.max(40, interval * 0.85);
        this.#repeatInterval = setTimeout(tick, interval);
      };
      tick();
    }, 400);
  }

  #handleButtonUp(): void {
    if (this.#repeatTimer) { clearTimeout(this.#repeatTimer); this.#repeatTimer = null; }
    if (this.#repeatInterval) { clearTimeout(this.#repeatInterval); this.#repeatInterval = null; }
  }

  // --- Drag helpers ---

  get #dragAxis(): 'x' | 'y' | 'none' {
    const val = this.getAttribute('drag');
    if (val === 'x' || val === 'y' || val === 'none') return val;
    // Default: stacked layout → vertical drag
    if (this.getAttribute('arrows') === 'stacked') return 'y';
    return 'x';
  }

  #getDragDelta(clientX: number, clientY: number): number {
    const axis = this.#dragAxis;
    if (axis === 'y') return this.#startY - clientY; // up = increase
    return clientX - this.#startX; // right = increase
  }

  #getDragMoved(clientX: number, clientY: number): number {
    const axis = this.#dragAxis;
    if (axis === 'y') return Math.abs(clientY - this.#startY);
    return Math.abs(clientX - this.#startX);
  }

  #applyMicroOffset(remainder: number, pxPerStep: number): void {
    if (!this.#valueEl) return;
    const offset = (remainder / pxPerStep) * 4;
    const axis = this.#dragAxis;
    this.#valueEl.style.transform = axis === 'y'
      ? `translateY(${-offset}px)`
      : `translateX(${offset}px)`;
  }

  #processDrag(clientX: number, clientY: number, shiftKey: boolean): void {
    const d = this.#getDragDelta(clientX, clientY);
    const step = this.step || 1;
    const effectiveStep = shiftKey ? step * 10 : step;
    const pxPerStep = shiftKey ? DRAG_PX_PER_STEP * 2 : DRAG_PX_PER_STEP;
    const steps = Math.round(d / pxPerStep);
    const newVal = this.#startValue + steps * effectiveStep;

    const remainder = d - steps * pxPerStep;
    this.#applyMicroOffset(remainder, pxPerStep);

    const clamped = this.hasAttribute('wrap') ? this.#wrapValue(newVal) : this.#clamp(newVal);
    const snapped = this.#snapToStep(clamped);

    if (snapped !== this.value) {
      this.value = snapped;
      this.#updateDisplay();
      this.#emitInput();
    }
  }

  // --- Mouse drag ---

  #handleMouseDown = (e: MouseEvent): void => {
    if (this.disabled || this.#editing || this.#dragAxis === 'none') return;
    e.preventDefault();
    this.#dragging = true;
    this.#startX = e.clientX;
    this.#startY = e.clientY;
    this.#startValue = this.value;
    this.setAttribute('data-dragging', '');
  };

  #handleMouseMove = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    this.#processDrag(e.clientX, e.clientY, e.shiftKey);
  };

  #handleMouseUp = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    const moved = this.#getDragMoved(e.clientX, e.clientY);
    this.#dragging = false;
    this.removeAttribute('data-dragging');

    if (this.#valueEl) {
      this.#valueEl.style.transform = '';
    }

    if (moved < CLICK_THRESHOLD && this.hasAttribute('editable')) {
      this.#enterEditMode();
    } else if (moved >= CLICK_THRESHOLD) {
      this.#emitChange();
    }
  };

  // --- Touch drag ---

  #handleTouchStart = (e: TouchEvent): void => {
    if (this.disabled || this.#editing || this.#dragAxis === 'none') return;
    e.preventDefault();
    this.#dragging = true;
    this.#startX = e.touches[0]?.clientX ?? 0;
    this.#startY = e.touches[0]?.clientY ?? 0;
    this.#startValue = this.value;
    this.setAttribute('data-dragging', '');
  };

  #handleTouchMove = (e: TouchEvent): void => {
    if (!this.#dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    this.#processDrag(touch.clientX, touch.clientY, false);
  };

  #handleTouchEnd = (): void => {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.removeAttribute('data-dragging');

    if (this.#valueEl) {
      this.#valueEl.style.transform = '';
    }

    this.#emitChange();
  };

  // --- Keyboard ---

  #handleKeydown = (e: KeyboardEvent): void => {
    if (this.disabled || this.#editing) return;

    const step = this.step || 1;
    const largeStep = step * 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        delta = e.shiftKey ? largeStep : step;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        delta = -(e.shiftKey ? largeStep : step);
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        delta = this.min - this.value;
        break;
      case 'End':
        delta = this.max - this.value;
        break;
      case 'Enter':
        if (this.hasAttribute('editable')) {
          this.#enterEditMode();
        }
        return;
      default:
        if (this.hasAttribute('editable') && /^[0-9.\-]$/.test(e.key)) {
          this.#enterEditMode(e.key);
          e.preventDefault();
        }
        return;
    }

    e.preventDefault();
    this.#adjustValue(delta);
    this.#emitInput();
    this.#emitChange();
  };

  // --- Wheel ---

  #handleWheel = (e: WheelEvent): void => {
    if (this.disabled || this.#editing) return;
    if (!this.matches(':hover') && !this.contains(document.activeElement)) return;

    e.preventDefault();
    const step = this.step || 1;
    const effectiveStep = e.shiftKey ? step * 10 : step;
    const delta = e.deltaY < 0 ? effectiveStep : -effectiveStep;
    this.#adjustValue(delta);
    this.#emitInput();
    this.#emitChange();
  };

  // --- Edit mode ---

  #enterEditMode(initialChar?: string): void {
    if (this.disabled || this.#editing) return;
    this.#editing = true;
    this.state = 'editing';

    if (this.#valueEl) this.#valueEl.style.display = 'none';
    if (this.#inputEl) {
      this.#inputEl.style.display = 'block';
      this.#inputEl.value = initialChar ?? this.#formatValue(this.value);
      this.#inputEl.focus();
      if (initialChar) {
        this.#inputEl.setSelectionRange(1, 1);
      } else {
        this.#inputEl.select();
      }
    }
  }

  #commitEdit(): void {
    if (!this.#editing) return;
    this.#editing = false;
    this.state = '';

    const raw = parseFloat(this.#inputEl?.value ?? '');
    if (!isNaN(raw)) {
      const clamped = this.#clamp(raw);
      const snapped = this.#snapToStep(clamped);
      this.value = snapped;
      this.#emitChange();
    }

    if (this.#valueEl) this.#valueEl.style.display = '';
    if (this.#inputEl) this.#inputEl.style.display = 'none';
    this.#updateDisplay();
    this.#display?.focus();
  }

  #cancelEdit(): void {
    if (!this.#editing) return;
    this.#editing = false;
    this.state = '';

    if (this.#valueEl) this.#valueEl.style.display = '';
    if (this.#inputEl) this.#inputEl.style.display = 'none';
    this.#display?.focus();
  }

  // --- Value management ---

  #adjustValue(delta: number): void {
    let newVal = this.value + delta;

    if (this.hasAttribute('wrap')) {
      newVal = this.#wrapValue(newVal);
    } else {
      newVal = this.#clamp(newVal);
    }

    newVal = this.#snapToStep(newVal);
    this.value = newVal;
    this.#updateDisplay();
  }

  #clamp(val: number): number {
    return Math.max(this.min, Math.min(this.max, val));
  }

  #wrapValue(val: number): number {
    const step = this.step || 1;
    const range = this.max - this.min + step;
    let wrapped = val;
    while (wrapped > this.max) wrapped -= range;
    while (wrapped < this.min) wrapped += range;
    return wrapped;
  }

  #snapToStep(val: number): number {
    const step = this.step;
    if (step <= 0) return this.#clamp(val);
    const snapped = Math.round((val - this.min) / step) * step + this.min;
    return this.#clamp(snapped);
  }

  #formatValue(val: number): string {
    const step = this.step;
    if (step >= 1) return Math.round(val).toString();
    const decimals = Math.max(0, -Math.floor(Math.log10(step)));
    return val.toFixed(decimals);
  }

  #updateDisplay(): void {
    if (this.#valueEl) {
      this.#valueEl.textContent = this.#formatValue(this.value);
    }
    this.#updateAria();
  }

  #updateAria(): void {
    if (this.#display) {
      this.#display.setAttribute('aria-valuenow', String(this.value));
      this.#display.setAttribute('aria-valuemin', String(this.min));
      this.#display.setAttribute('aria-valuemax', String(this.max));
    }
  }

  #emitInput(): void {
    this.emit('input', { value: this.value });
  }

  #emitChange(): void {
    this.emit('change', { value: this.value });
  }

  // --- Public API ---

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? '0');
  }
  set value(val: number) {
    this.setAttribute('value', String(val));
  }

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '1');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get drag(): 'x' | 'y' | 'none' {
    return this.#dragAxis;
  }
  set drag(val: 'x' | 'y' | 'none') {
    this.setAttribute('drag', val);
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === 'value' && this.#display) {
      this.#updateDisplay();
    }
  }

  teardown(): void {
    this.#handleButtonUp();
  }
}

customElements.define('ry-number-select', RyNumberSelect);
